<template>
  <div class="container">
    <div class="mx-auto p-10 w-3/4">
      <form class="">
        <div class="grid grid-cols-1 md:grid-cols-1 gap-5 mb-3">
          <!-- Facility Logo Section -->
          <h2 class="text-lg font-bold">Facility Logo</h2>
          <div class="grid grid-cols-12 gap-5">
            <div class="col-span-12 md:col-span-6 border-r-2 border-r-gray-300 pr-10">
              <div class="grid grid-cols-12 gap-5">
                <div class="col-span-12 md:col-span-6 relative bg-gray-300">
                  <img :src="`${facility.logo}`" class="w-30 h-18 rounded-full m-2"
                    alt="Facility logo" />
                  <div v-if="uploading" class="absolute inset-0 flex items-center justify-center">
                    <div class="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-custom-blue"></div>
                  </div>
                </div>
                <div class="col-span-12 md:col-span-6">
                  <div class="flex flex-col items-center">
                    <input type="file" ref="fileInput" @change="handleFileChange" accept="image/*" class="hidden" />
                    <button type="button" @click="triggerFileInput"
                      class="px-4 py-2 rounded dark:text-custom-blue border-2 border-custom-blue mb-2 w-full"
                      :disabled="uploading">
                      Change Photo
                    </button>
                    <CustomButton label="Remove" :disabled="uploading"
                      bg-class="bg-custom-blue px-8 py-3 text-center w-full" @click="handleRemovePhoto" />
                  </div>
                </div>
              </div>
            </div>
            <div class="col-span-12 md:col-span-6 px-10">
              <h2 class="text-lg">Image requirements</h2>
              <ol class="list-decimal px-5">
                <li>Min. 100 x 50px</li>
                <li>Max. 2MB</li>
                <li>Your personal image</li>
              </ol>
              <div v-if="error" class="mt-4 p-3 bg-red-100 text-red-700 rounded">
                {{ error }}
              </div>
            </div>
          </div>

          <!-- Facility Information -->
          <h2 class="text-lg font-bold">Facility Information</h2>
        </div>

        <div class="grid grid-cols-1 gap-5 mb-3">
          <div class="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label class="block mb-1">Hospital name <span class="text-red-500">*</span></label>
              <input type="text" v-model="formData.name"
                class="w-full p-2.5 border rounded bg-gray-100 placeholder-gray-400 shadow-sm shadow-gray-300"
                placeholder="Enter Facility name" required>
            </div>
            <div>
              <label class="block mb-1">Contact Phone Number</label>
              <input type="text" v-model="formData.contact_person_phone"
                class="w-full p-2.5 border rounded bg-gray-100 placeholder-gray-400 shadow-sm shadow-gray-300"
                placeholder="Enter contact phone number" required>
            </div>

            <div>
              <label class="block mb-1">Contact Email <span class="text-red-500">*</span></label>
              <input type="email" v-model="formData.email"
                class="w-full p-2.5 border rounded bg-gray-100 placeholder-gray-400 shadow-sm shadow-gray-300"
                placeholder="Enter facility email" required>
            </div>

            <div>
              <label class="block mb-1">Facility Type</label>
              <select v-model="formData.facility_type"
                class="w-full p-3 border rounded bg-gray-100 placeholder-gray shadow-sm shadow-gray-400 appearance-none"
                required>
                <option value="" disabled>Select facility type</option>
                <option value="Private">Private</option>
                <option value="Public">Public</option>
              </select>
            </div>

            <div>
              <label class="block mb-1">Country <span class="text-red-500">*</span></label>
              <div class="relative">
                <select id="country_id" v-model="formData.country_id"
                  class="w-full p-3 border rounded bg-gray-100 placeholder-gray shadow-sm shadow-gray-400 appearance-none"
                  required>
                  <option value="" disabled>Select country</option>
                  <option v-for="region in regions" :key="region.country_id" :value="region.country_id">
                    {{ region.name }}
                  </option>
                </select>
                <div class="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                  <svg class="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                    <path
                      d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" />
                  </svg>
                </div>
              </div>
            </div>

            <div>
              <label class="block mb-1">County <span class="text-red-500">*</span></label>
              <div class="relative">
                <select id="county_id" v-model="formData.county_id"
                  class="w-full p-3 border rounded bg-gray-100 placeholder-gray shadow-sm shadow-gray-400 appearance-none"
                  required>
                  <option value="" disabled>Select county</option>
                  <option v-for="county in filteredCounties" :key="county.county_id" :value="county.county_id">
                    {{ county.name }}
                  </option>
                </select>
                <div class="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                  <svg class="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                    <path
                      d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" />
                  </svg>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-5 mb-3">
          <div>
            <label class="block mb-1">Longitude<span class="text-red-500">*</span></label>
            <input type="text" v-model="formData.longitude"
              class="w-full p-2.5 border rounded bg-gray-100 placeholder-gray-400 shadow-sm shadow-gray-300"
              placeholder="Enter facility longitude" required>
          </div>
          <div>
            <label class="block mb-1">Latitude<span class="text-red-500">*</span></label>
            <input type="text" v-model="formData.latitude"
              class="w-full p-2.5 border rounded bg-gray-100 placeholder-gray-400 shadow-sm shadow-gray-300"
              placeholder="Enter facility latitude" required>
          </div>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div>
            <label class="block mb-1">Package<span class="text-red-500">*</span></label>
            <input type="text" v-model="formData.account_status"
              class="w-full p-2.5 border rounded bg-gray-100 placeholder-gray-400 shadow-sm shadow-gray-300"
              placeholder="Select package" required>
          </div>
        </div>
      </form>

      <div class="flex items-center justify-between mt-8">
        <div class="flex items-center ml-auto">
          <button @click="resetForm" class="px-4 py-2 border rounded text-orange-500 border-orange-500">
            Cancel
          </button>
          <CustomButton label="Update" bg-class="bg-custom-yellow px-8 py-3 ml-3" @click="handleSubmitUpdateFacility" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import AWS from 'aws-sdk';
import CustomButton from "@/components/CustomButton.vue";
import api from '@/services/api';
import SweetAlertService from '@/services/SweetAlertService';

export default {
  name: 'FacilityProfileTab',
  components: {
    CustomButton,
  },
  data() {
    return {
      counties: [],
      userData: {},
      formData: {
        name: '',
        email: '',
        facility_type: '',
        contact_person_name: '',
        contact_person_phone: '',
        country_id: '',
        county_id: '',
        second_level_devision: '',
        third_level_devision: '',
        longitude: '',
        latitude: '',
        account_status: '',
      },
      status: 'active',
      error: null,
      facility: [],
      regions: [],
      uploading: false,
      previewImage: null,
      defaultImage: '/api/placeholder/96/96',
      selectedFile: null,
    }
  },
  created() {
    this.fetchRegions();
  },
  computed: {
    filteredCounties() {
      const selectedCountry = this.regions.find(region => region.country_id === this.formData.country_id);
      return selectedCountry ? selectedCountry.counties : [];
    }
  },
  mounted() {
    this.userData = this.$store.getters["auth/user"];
    this.populateFormData();
  },
  methods: {
    triggerFileInput() {
      this.$refs.fileInput.click();
    },

    async validateImage(file) {
      // Check file size (2MB max)
      const maxSize = 2 * 1024 * 1024; // 2MB in bytes
      if (file.size > maxSize) {
        throw new Error('File size must be less than 2MB');
      }

      // Check image dimensions
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.src = URL.createObjectURL(file);

        img.onload = () => {
          URL.revokeObjectURL(img.src);
          if (img.width < 100 || img.height < 50) {
            reject(new Error('Image dimensions must be at least 100x50px'));
            return;
          }
          resolve();
        };

        img.onerror = () => {
          URL.revokeObjectURL(img.src);
          reject(new Error('Invalid image file'));
        };
      });
    },

    async handleFileChange(event) {
      const file = event.target.files?.[0];
      if (!file) return;

      try {
        this.error = null;
        this.uploading = true;

        // Validate the image
        await this.validateImage(file);

        // Create preview
        this.previewImage = URL.createObjectURL(file);
        this.selectedFile = file;
      } catch (err) {
        this.error = err.message;
        this.previewImage = this.defaultImage;
      } finally {
        this.uploading = false;
        event.target.value = ''; // Reset file input
      }
    },

    async handleRemovePhoto() {
      try {
        this.error = null;
        this.uploading = true;
        this.previewImage = this.defaultImage; // Reset the preview image
        this.selectedFile = null; // Clear the selected file

        // Update facility with removed logo
        await this.updateFacilityLogo(null);

        await SweetAlertService.showSuccess('Success!', 'Facility logo removed successfully!');
      } catch (err) {
        this.error = err.message;
        await SweetAlertService.showError('Error', 'Failed to remove facility logo.');
      } finally {
        this.uploading = false;
      }
    },

    async uploadFile() {
      if (!this.selectedFile) return null;

      const s3 = new AWS.S3({
        endpoint: 'https://blr1.digitaloceanspaces.com',
        signatureVersion: 'v4',
        accessKeyId: 'DO00PK2KLQ4HUBKTB6R3',
        secretAccessKey: 'L3yOKmH3Dqfpc3tVZv4Mz5O+jd1bXTobKS//zmY72Rk',
        region: 'blr1',
      });

      const params = {
        Bucket: 'jiji-bucket',
        Key: `facility-logos/${Date.now()}-${this.selectedFile.name}`,
        Body: this.selectedFile,
        ACL: 'public-read',
        ContentType: this.selectedFile.type,
      };

      try {
        const result = await s3.upload(params).promise();
        return result.Location;
      } catch (error) {
        throw new Error('Failed to upload file. Please check your internet connection.');
      }
    },

    async updateFacilityLogo(logoUrl) {
      try {
        await api.updateFacility(this.facility.facility_id, { logo_url: logoUrl });
      } catch (error) {
        throw new Error('Failed to update facility logo');
      }
    },

    validateFields() {
      const emptyFields = [];
      if (!this.formData.name) emptyFields.push('Facility name');
      if (!this.formData.email) emptyFields.push('First email');
      if (!this.formData.facility_type) emptyFields.push('Facility Type');
      if (!this.formData.contact_person_name) emptyFields.push('Contact person name');
      if (!this.formData.contact_person_phone) emptyFields.push('Contact person phone');
      if (!this.formData.country_id) emptyFields.push('Country');
      if (!this.formData.county_id) emptyFields.push('Sub-county');
      // if (!this.formData.third_level_devision) emptyFields.push('Address');
      if (!this.formData.account_status) emptyFields.push('Account status');

      return emptyFields;
    },

    populateFormData() {
      if (Array.isArray(this.userData.user_roles) && this.userData.user_roles.length > 0) {
        this.facility = this.userData.user_roles[0].facility_department.facility;

        this.formData.name = this.facility.name || '';
        this.formData.email = this.facility.email || '';
        this.formData.facility_type = this.facility.facility_type || '';
        this.formData.contact_person_name = this.facility.contact_person_name || '';
        this.formData.contact_person_phone = this.facility.contact_person_phone || '';
        this.formData.country_id = this.facility.country_id || '';
        this.formData.county_id = this.facility.county_id || '';
        this.formData.second_level_devision = this.facility.second_level_devision || '';
        // this.formData.third_level_devision = this.facility.third_level_devision || '';
        this.formData.longitude = this.facility.longitude || '';
        this.formData.latitude = this.facility.latitude || '';
        this.formData.account_status = this.facility.account_status || '';

        // Set the initial logo if it exists
        const logoUrl = this.userData.user_roles[0].facility_department.logo; // Ensure this path is correct
        this.previewImage = logoUrl || this.defaultImage;
      } else {
        console.warn('user_roles is undefined or empty');
      }
    },

    async fetchRegions() {
      try {
        const response = await api.getRegions();
        this.regions = response.regions || [];
      } catch (error) {
        console.error('Error fetching regions:', error);
      }
    },

    resetForm() {
      this.populateFormData();
      this.error = null;
    },

    async handleSubmitUpdateFacility() {
      try {
        const emptyFields = this.validateFields();
        if (emptyFields.length > 0) {
          throw new Error(`Please fill in the following required fields: ${emptyFields.join(', ')}`);
        }

        const data = {
          name: this.formData.name,
          email: this.formData.email,
          facility_type: this.formData.facility_type,
          contact_person_name: this.formData.contact_person_name,
          contact_person_phone: this.formData.contact_person_phone,
          country_id: this.formData.country_id,
          county_id: this.formData.county_id,
          second_level_devision: this.formData.second_level_devision,
          // third_level_devision: this.formData.third_level_devision,
          longitude: this.formData.longitude,
          latitude: this.formData.latitude,
          account_status: this.formData.account_status,
        };

        // Check if there is a selected file
        if (this.selectedFile) {
          const fileUrl = await this.uploadFile();
          if (!fileUrl) {
            this.error = 'Failed to upload logo';
            return;
          }
          data.logo = fileUrl; // Add the logo URL to the data
        }

        await api.updateFacility(this.facility.facility_id, data);
        await SweetAlertService.showSuccess('Success!', 'Facility updated successfully!');
        this.$router.go(0);
      } catch (error) {
        this.error = error.message || 'Failed to update facility';
        console.error('error:', this.error);
        await SweetAlertService.showError('Error', this.error);
      }
    }
  },
}
</script>

<style lang="css" scoped>
/* Add any custom styles here */
.custom-file-upload {
  cursor: pointer;
}

/* Ensure proper spacing for error messages */
.error-message {
  margin-top: 0.5rem;
  color: #ef4444;
}

/* Custom transition for loading states */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>